/**
 * Analytics API Client
 *
 * API client for usage analytics endpoints.
 * Extends BaseApiClient for authentication, error handling, and type-safe requests.
 */

import { getCurrentOrgIdFromStore } from '@/stores/orgIdAccessor';
import type {
  UsageAnalytics,
  UsageAnalyticsQuery,
  UserUsageAnalyticsParams,
  UserUsageAnalyticsResponse,
  ApplicationUsageAnalyticsParams,
  ApplicationUsageAnalyticsResponse,
} from '@/types/analytics.types';
import type {
  ApplicationAnalyticsResponse,
  ApplicationSessionsResponse,
  AnalyticsQueryParams,
  SessionsQueryParams,
} from '@/types/publisherAnalytics.types';
// GW-6534: Read currentOrg.id from the Zustand store rather than localStorage
// to keep a single source of truth across the app.

import { BaseApiClient } from './baseApiClient';

class AnalyticsApiClient extends BaseApiClient {
  // ============================================================================
  // Public API Methods
  // ============================================================================

  /**
   * Get usage analytics with filters
   *
   * API: GET /v1/analytics/usage
   * Query params: user_id, application_id, start_date, end_date, granularity
   *
   * Authorization: Regular users see own data, org admins see all users in tenant
   *
   * @param query - Analytics query parameters
   * @returns Usage analytics with summary and breakdown
   * @throws {ApiError} When API request fails
   *
   * @example
   * ```typescript
   * const analytics = await analyticsApi.getUsageAnalytics({
   *   application_id: 'app-123',
   *   start_date: '2025-11-01',
   *   end_date: '2025-12-01',
   *   granularity: 'daily'
   * });
   * ```
   */
  async getUsageAnalytics(query: UsageAnalyticsQuery): Promise<UsageAnalytics> {
    // GW-6534: org_id is required by the backend as a query parameter for
    // non-global_admin callers, so we default to the Zustand store
    // (canonical source of truth) instead of localStorage when the caller
    // did not express a preference.
    //
    // GW-8512: `query.org_id === null` is an explicit "All Organizations"
    // signal from a global_admin — it must NOT be overridden with the
    // current org, or the request always comes back scoped to that org and
    // the org_breakdown/organization-switcher data never loads. Only
    // `undefined` (no preference expressed) falls back to the store.
    const orgId = query.org_id === null ? undefined : (query.org_id ?? getCurrentOrgIdFromStore());
    const queryString = this.buildQueryString({
      org_id: orgId || undefined,
      user_id: query.user_id,
      application_id: query.application_id,
      start_date: query.start_date,
      end_date: query.end_date,
      granularity: query.granularity,
    });

    // GW-327: Remove /v1 prefix since VITE_API_BASE_URL already includes it
    return this.get<UsageAnalytics>(`/analytics/usage${queryString}`);
  }

  /**
   * Get analytics for specific application
   *
   * @param applicationId - Application ID
   * @param query - Analytics query parameters
   */
  async getApplicationAnalytics(
    applicationId: string,
    query?: Omit<UsageAnalyticsQuery, 'application_id'>
  ): Promise<UsageAnalytics> {
    return this.getUsageAnalytics({ ...query, application_id: applicationId });
  }

  /**
   * Get analytics for specific user (convenience method using main endpoint)
   *
   * @param userId - User ID
   * @param query - Analytics query parameters
   */
  async getUserAnalytics(
    userId: string,
    query?: Omit<UsageAnalyticsQuery, 'user_id'>
  ): Promise<UsageAnalytics> {
    return this.getUsageAnalytics({ ...query, user_id: userId });
  }

  /**
   * Get user-specific usage analytics
   *
   * API: GET /v1/analytics/usage/user
   * Query params: user_id, period, granularity
   *
   * Authorization: Users can see their own data, org admins can see any user
   *
   * @param params - User analytics query parameters
   * @returns User-specific usage analytics
   * @throws {ApiError} When API request fails
   *
   * @example
   * ```typescript
   * const analytics = await analyticsApi.getUserUsageAnalytics({
   *   user_id: 'user-123',
   *   period: '30d',
   *   granularity: 'daily'
   * });
   * ```
   */
  async getUserUsageAnalytics(
    params: UserUsageAnalyticsParams
  ): Promise<UserUsageAnalyticsResponse> {
    const queryString = this.buildQueryString({
      user_id: params.user_id,
      period: params.period,
      granularity: params.granularity,
    });

    // GW-327: Remove /v1 prefix since VITE_API_BASE_URL already includes it
    return this.get<UserUsageAnalyticsResponse>(`/analytics/usage/user${queryString}`);
  }

  /**
   * Get application-specific usage analytics
   *
   * API: GET /v1/analytics/usage/application
   * Query params: app_id, org_id, period, granularity
   *
   * Authorization: Org admins can see application usage within their org
   *
   * @param params - Application analytics query parameters
   * @returns Application-specific usage analytics
   * @throws {ApiError} When API request fails
   *
   * @example
   * ```typescript
   * const analytics = await analyticsApi.getApplicationUsageAnalytics({
   *   app_id: 'app-123',
   *   period: '30d',
   *   granularity: 'daily'
   * });
   * ```
   */
  async getApplicationUsageAnalytics(
    params: ApplicationUsageAnalyticsParams
  ): Promise<ApplicationUsageAnalyticsResponse> {
    const queryString = this.buildQueryString({
      app_id: params.app_id,
      org_id: params.org_id,
      period: params.period,
      granularity: params.granularity,
    });

    // GW-327: Remove /v1 prefix since VITE_API_BASE_URL already includes it
    return this.get<ApplicationUsageAnalyticsResponse>(
      `/analytics/usage/application${queryString}`
    );
  }

  // ============================================================================
  // Publisher Analytics Methods (GW-1538)
  // ============================================================================

  /**
   * Get publisher analytics for a specific application
   *
   * API: GET /applications/{appId}/analytics
   * Query params: startDate, endDate
   *
   * Authorization: app_admin, org_admin, or global_admin role required
   *
   * @param appId - Application ID
   * @param params - Analytics query parameters (optional)
   * @returns Aggregated metrics with period comparison and daily breakdown
   * @throws {ApiError} When API request fails
   *
   * @example
   * ```typescript
   * const analytics = await analyticsApi.getPublisherAnalytics('app-123', {
   *   startDate: '2025-01-01',
   *   endDate: '2025-01-31'
   * });
   * ```
   */
  async getPublisherAnalytics(
    appId: string,
    params?: AnalyticsQueryParams
  ): Promise<ApplicationAnalyticsResponse> {
    const queryString = this.buildQueryString({
      startDate: params?.startDate,
      endDate: params?.endDate,
    });

    return this.get<ApplicationAnalyticsResponse>(`/applications/${appId}/analytics${queryString}`);
  }

  /**
   * Get paginated sessions list for a specific application
   *
   * API: GET /applications/{appId}/sessions
   * Query params: startDate, endDate, status, search, page, pageSize
   *
   * Authorization: app_admin, org_admin, or global_admin role required
   *
   * @param appId - Application ID
   * @param params - Sessions query parameters (optional)
   * @returns Paginated sessions list
   * @throws {ApiError} When API request fails
   *
   * @example
   * ```typescript
   * const sessions = await analyticsApi.getPublisherSessions('app-123', {
   *   startDate: '2025-01-01',
   *   endDate: '2025-01-31',
   *   status: 'active',
   *   page: 1,
   *   pageSize: 10
   * });
   * ```
   */
  async getPublisherSessions(
    appId: string,
    params?: SessionsQueryParams
  ): Promise<ApplicationSessionsResponse> {
    const queryString = this.buildQueryString({
      startDate: params?.startDate,
      endDate: params?.endDate,
      status: params?.status && params.status !== 'all' ? params.status : undefined,
      search: params?.search,
      page: params?.page,
      pageSize: params?.pageSize,
    });

    return this.get<ApplicationSessionsResponse>(`/applications/${appId}/sessions${queryString}`);
  }
}

// ============================================================================
// Export Singleton Instance
// ============================================================================

export const analyticsApi = new AnalyticsApiClient();
